// BUILD-049 (supporting model): the "HR tasks" section of My Work - the checklist tasks (onboarding AND offboarding) that HR has named the signed-in Employee to do.
//
// The HR checklist row is the only record. This section shows that row's minimal projection (GET /hr/me/assigned-tasks) and sends start / note / Done to the same row
// (POST /hr/me/assigned-tasks/:id/progress). It adds nothing to the BUILD-046 lists, counts or presets and fails on its own: an outage here never touches the other work.
//
// Pure module: no React, no I/O. The only runtime import is the page's one overdue date test. Erasable TypeScript only (it runs under node --experimental-strip-types in the frontend model tests).

import { isOverdueTask } from './overdueRule';

export type HrTaskStatus = 'not_started' | 'in_progress' | 'completed' | 'exception';
export type HrChecklistKind = 'onboarding' | 'offboarding';

export interface HrTaskRow {
  assignmentTaskId: string;
  title: string;
  checklistName: string;
  checklistKind: HrChecklistKind;
  forEmployee: { employeeId: string; displayName: string | null };
  assignedBy: { displayName: string | null };
  dueDate: string;
  status: HrTaskStatus;
  completedAt: string | null;
  taskNote: string | null;
  documentLinked: boolean;
  capabilities: { canStart: boolean; canNote: boolean; canComplete: boolean };
}

export interface HrTasksPage {
  tasks: HrTaskRow[];
  page: { state: 'open' | 'done'; limit: number; offset: number; total: number; nextOffset?: number };
}

export type HrTasksPhase = 'idle' | 'loading' | 'ready' | 'error';
export type HrTasksView = 'open' | 'done';

export interface HrTasksState {
  phase: HrTasksPhase;
  view: HrTasksView;
  tasks: readonly HrTaskRow[];
  /** The server's exact count for the current view (so "Showing 3 of 7" is true). */
  total: number;
  nextOffset: number | null;
  /** The honest reason the section could not load; the rows below it (if any) are the last ones loaded. */
  error: string | null;
  /** A silent re-read failed: the rows are the last ones loaded. */
  refreshNote: string | null;
  /** Per-row action error text and in-flight flags. */
  rowError: Readonly<Record<string, string>>;
  busy: Readonly<Record<string, true>>;
  /** True once the section has shown rows in this page's life, so finishing the last task does not make the section vanish mid-action. */
  everShown: boolean;
}

export function initialHrTasksState(): HrTasksState {
  return { phase: 'idle', view: 'open', tasks: [], total: 0, nextOffset: null, error: null, refreshNote: null, rowError: {}, busy: {}, everShown: false };
}

export const UNAVAILABLE_MESSAGE = "HR tasks can't be shown right now. Your other work is not affected.";
export const NO_LONGER_YOURS_MESSAGE = 'This task is no longer assigned to you. Your list has been refreshed.';
export const NOTE_VISIBILITY_LABEL = 'Task note \u2014 shared with HR and anyone assigned this task';
export const DOCUMENT_LOCK_TEXT = 'Signed forms and approvals are completed through the HR document review, not here.';

export type HrSectionView =
  | { kind: 'hidden' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'rows'; rows: readonly HrTaskRow[]; total: number; showing: number; hasMore: boolean; refreshNote: string | null; view: HrTasksView };

/**
 * What the host renders. Nothing while the first answer has not arrived and nothing for a person with no assigned HR task (so the other layouts do not move);
 * an honest message when the read failed and there is nothing to show; otherwise the rows with a truthful "showing N of M".
 */
export function describeHrSection(state: HrTasksState): HrSectionView {
  if (state.phase === 'idle') return { kind: 'hidden' };
  if (state.phase === 'loading' && state.tasks.length === 0 && !state.everShown) return { kind: 'hidden' };
  if (state.phase === 'error' && state.tasks.length === 0) return { kind: 'error', message: state.error ?? UNAVAILABLE_MESSAGE };
  if (state.tasks.length === 0 && state.total === 0 && !state.everShown) return { kind: 'hidden' };
  return {
    kind: 'rows', rows: state.tasks, total: state.total, showing: state.tasks.length, hasMore: state.nextOffset !== null,
    refreshNote: state.phase === 'error' ? (state.error ?? UNAVAILABLE_MESSAGE) : state.refreshNote, view: state.view,
  };
}

/** Add rows from a later page without repeating any row (offset paging can shift under a change). */
export function appendPage(existing: readonly HrTaskRow[], more: readonly HrTaskRow[]): HrTaskRow[] {
  const seen = new Set(existing.map((r) => r.assignmentTaskId));
  return [...existing, ...more.filter((r) => !seen.has(r.assignmentTaskId))];
}

/** Replace one row by the server's answer. A row that left the current view (completed while looking at "open") is removed and the total reduced. */
export function mergeRow(state: HrTasksState, row: HrTaskRow): HrTasksState {
  const open = row.status === 'not_started' || row.status === 'in_progress';
  const belongs = state.view === 'open' ? open : !open;
  const has = state.tasks.some((r) => r.assignmentTaskId === row.assignmentTaskId);
  if (!has) return state;
  if (belongs) return { ...state, tasks: state.tasks.map((r) => (r.assignmentTaskId === row.assignmentTaskId ? row : r)) };
  return { ...state, tasks: state.tasks.filter((r) => r.assignmentTaskId !== row.assignmentTaskId), total: Math.max(0, state.total - 1) };
}

export interface HrTaskOffers {
  start: boolean;
  note: boolean;
  done: boolean;
  /** Shown instead of Done on a document-linked task that is still open. */
  documentLock: boolean;
}

/** The buttons a row offers. The server decides (capabilities); the screen only reflects it. */
export function taskOffers(row: HrTaskRow): HrTaskOffers {
  const open = row.status === 'not_started' || row.status === 'in_progress';
  return {
    start: open && row.capabilities.canStart,
    note: open && row.capabilities.canNote,
    done: open && row.capabilities.canComplete,
    documentLock: open && row.documentLinked && !row.capabilities.canComplete,
  };
}

export function kindLabel(kind: HrChecklistKind): string {
  return kind === 'offboarding' ? 'Offboarding' : 'Onboarding';
}

export function statusLabel(status: HrTaskStatus): string {
  return status === 'not_started' ? 'Not started' : status === 'in_progress' ? 'In progress' : status === 'completed' ? 'Completed' : 'Not needed';
}

/** "for Jane Fictional - New hire checklist": who the task is about and which checklist it belongs to. Never more than the projection carries. */
export function contextLine(row: HrTaskRow): string {
  const who = row.forEmployee.displayName && row.forEmployee.displayName.trim() !== '' ? row.forEmployee.displayName : 'an employee';
  return `For ${who} \u00b7 ${row.checklistName}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Nov 2" from YYYY-MM-DD, with no time zone arithmetic. */
export function dueText(dueDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dueDate);
  if (!m) return dueDate;
  return `${MONTHS[Number(m[2]) - 1] ?? m[2]} ${Number(m[3])}`;
}

/**
 * Overdue = THE page rule (overdueRule.isOverdueTask) applied to an HR task: it is open while not started or in progress (the rule's "active" / "in progress"), and a finished or
 * "not needed" task is never overdue. This file compares no date itself.
 */
export function isOverdue(row: Pick<HrTaskRow, 'status' | 'dueDate'>, todayPhoenix: string): boolean {
  const status = row.status === 'not_started' ? 'active' : row.status === 'in_progress' ? 'in_progress' : 'completed';
  return isOverdueTask({ status, dueDate: row.dueDate }, todayPhoenix);
}

/** Today's calendar date in America/Phoenix (YYYY-MM-DD), the company's date. */
export function phoenixToday(now: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Phoenix', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** Map a failed action to the plain sentence shown on the row. `status` is the HTTP status the client attached; `message` is the server's own text. */
export function actionErrorText(thrown: unknown): { text: string; refresh: boolean } {
  const e = thrown as { status?: number; message?: string } | null;
  if (e && e.status === 404) return { text: NO_LONGER_YOURS_MESSAGE, refresh: true };
  if (e && e.status === 409 && typeof e.message === 'string' && e.message !== '') return { text: e.message, refresh: true };
  if (e && (e.status === 400 || e.status === 403) && typeof e.message === 'string' && e.message !== '') return { text: e.message, refresh: false };
  return { text: 'That change was not saved. Try again.', refresh: false };
}
